/**
 * Real blueprint routes.
 *
 * Server contract (see `crates/gi-api-server/src/routes/blueprints.rs`):
 *
 *   POST /blueprints
 *        body:     EntityRecord { manifest, signed_entity, contract }
 *        → { root_id, sequence }
 *        rejects 422 if `contract.parameters` is empty.
 *
 *   GET  /blueprints
 *        → { rows: BlueprintRow[], total }
 *        BlueprintRow = { root_id, name, version, domain, parameters[], sequence }
 *
 *   POST /blueprints/{id}/instantiate
 *        body: { bytecode_hex, parameters?, inputs_hex?, monotonic_tick? }
 *        → { root_id, outputs_hex, fuel_consumed, duration_ns, wasmtime_version }
 *
 * Shape-gap notes:
 *
 *   - The server treats a "blueprint" as a stored GI whose contract
 *     declares ≥1 parameter. The UI's `Blueprint` type is a DAG of
 *     nodes + edges (the Conductor Builder surface), NOT the same
 *     concept. The two overlap when the user "Saves as Conductor":
 *     we serialise the DAG into an EntityRecord and POST it.
 *
 *   - The server's list endpoint returns metadata only — no DAG, no
 *     edges. `listBlueprints` here maps every server row into a stub
 *     `Blueprint` with empty `nodes` + `edges`. A user listing server
 *     blueprints in the Builder UI sees them as "existing Conductors
 *     in the engine", not as editable local drafts.
 *
 *   - `publishBlueprintAsConductor` has to inject a synthetic `trigger`
 *     parameter because the server rejects parameterless contracts.
 *     This is cosmetic: the Conductor DAG semantically "triggers" on
 *     an external signal; we model that as a single Bool parameter.
 *     Clean up when the server grows a dedicated "conductor" entity
 *     type that does not require parameters.
 */

import { bytesToHex } from '@noble/hashes/utils';

import type {
  ApiCreateGiResponse,
  BehavioralContract,
  Blueprint,
  BlueprintInstantiateRequest,
  BlueprintInstantiateResponse,
  DomainType,
  Ed25519Signature,
  EntityId,
  Manifest,
  ParameterDef,
  PortDefinition,
  SignedEntity,
  TrustedTimestamp,
} from '../types';
import { ApiHttpError, apiFetch } from '../http';
import {
  canonicalHash,
  generateKeyPair,
  randomHex,
  signDigest,
} from '../mock/sign';
import { appendActivity, upsertRecord, type GiRecord } from '../mock/store';

interface ServerBlueprintRow {
  root_id: string;
  name: string;
  version: string;
  domain: string;
  parameters: string[];
  sequence: number;
}

interface ServerBlueprintsResponse {
  rows: ServerBlueprintRow[];
  total: number;
}

export async function listBlueprints(): Promise<Blueprint[]> {
  const response = await apiFetch<ServerBlueprintsResponse>('/blueprints');
  return response.rows.map(rowToBlueprintStub);
}

export async function publishBlueprintAsConductor(
  blueprint: Blueprint,
): Promise<string> {
  const kp = generateKeyPair();
  const root_id = randomUuid();
  const created_at: TrustedTimestamp = {
    iso: new Date().toISOString(),
    source: 'Monotonic',
  };
  const domain: DomainType = { Custom: 'conductor' };

  // Derive entry + terminal nodes to synthesise conductor-level ports.
  const entries = new Set(blueprint.nodes.map((n) => n.id));
  const terminals = new Set(blueprint.nodes.map((n) => n.id));
  for (const e of blueprint.edges) {
    entries.delete(e.target_node);
    terminals.delete(e.source_node);
  }
  const conductorInputs: PortDefinition[] = [];
  const conductorOutputs: PortDefinition[] = [];
  for (const n of blueprint.nodes) {
    if (entries.has(n.id)) conductorInputs.push(...n.inputs);
    if (terminals.has(n.id)) conductorOutputs.push(...n.outputs);
  }

  // Synthetic parameter required by the server (contract.parameters
  // must be non-empty). See module-level shape-gap note.
  const triggerParam: ParameterDef = {
    name: 'trigger',
    description:
      'Conductor trigger signal. Set to `true` to instantiate; the server accepts the blueprint only because this parameter exists.',
    default: { bool: true },
  };

  const contract: BehavioralContract = {
    inputs: conductorInputs,
    outputs: conductorOutputs,
    parameters: [triggerParam],
    behavior_rules: [],
    domain_adapter: domain,
    macros: [],
  };
  const contract_hash = canonicalHash(contract);

  const manifest: Manifest = {
    gi_version: '0.1.0',
    root_id,
    version: '1.0.0',
    name: blueprint.name,
    description: blueprint.description,
    domain,
    author: { name: 'you', public_key: kp.public_key },
    contract_hash,
    // `source_hash` commits to the Conductor DAG bytes without
    // embedding them in the manifest. A future engine revision will
    // archive the DAG separately keyed by this hash.
    source_hash: canonicalHash(blueprint),
    wasm_hash: `sha256:${randomHex(32)}`,
    compiler: { name: 'conductor-builder', version: '0.1.0', target: 'none' },
    lineage: { root_id, depth: 0 },
    created_at,
  };

  const manifest_hash = canonicalHash(manifest);
  const signature: Ed25519Signature = signDigest(
    hexToBytes(manifest_hash.slice('sha256:'.length)),
    kp,
  );
  const signed_entity: SignedEntity = {
    root_id,
    version: manifest.version,
    manifest_hash,
    signature,
    signer_public_key: kp.public_key,
    created_at,
  };

  await apiFetch<ApiCreateGiResponse>('/blueprints', {
    method: 'POST',
    body: { manifest, signed_entity, contract },
  });

  // Mirror into the mock store so the Vault's snapshot view (still
  // mock-backed — no `/vault` server route yet) picks up the new
  // Conductor card.
  const record: GiRecord = {
    manifest,
    signed_entity,
    contract,
    lineage: { root_id, entries: [] },
    attestations: [],
    statuses: ['signed'],
    counters: { signatures: 1, verifications: 0, executions: 0, forks: 0 },
    secret_key_hex: bytesToHex(kp.secret_key),
  };
  upsertRecord(record);
  appendActivity({
    kind: 'sparked',
    root_id,
    name: manifest.name,
    at: created_at,
  });

  return root_id;
}

/** POST /blueprints/{id}/instantiate — execute a blueprint with
 *  parameter overrides. New surface (no mock counterpart). */
export async function instantiateBlueprint(
  root_id: EntityId,
  request: BlueprintInstantiateRequest,
): Promise<BlueprintInstantiateResponse> {
  try {
    return await apiFetch<BlueprintInstantiateResponse>(
      `/blueprints/${encodeURIComponent(root_id)}/instantiate`,
      { method: 'POST', body: request },
    );
  } catch (err) {
    if (err instanceof ApiHttpError) throw err;
    throw err;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────

/** Convert a server row into a UI `Blueprint` with empty nodes/edges.
 *  The server never stored the DAG, so we surface only what it knows. */
function rowToBlueprintStub(row: ServerBlueprintRow): Blueprint {
  return {
    name: row.name,
    description: `Server blueprint ${row.root_id.slice(0, 8)}… (v${row.version}, ${row.parameters.length} parameter${row.parameters.length === 1 ? '' : 's'}, seq ${row.sequence}).`,
    error_policy: 'fail_fast',
    nodes: [],
    edges: [],
  };
}

function randomUuid(): EntityId {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const hex = randomHex(16);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `8${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}
